import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, LayoutGrid, ArrowRight, LifeBuoy, HelpCircle, Mail, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { BRAND } from "@/data/site";
import { INDUSTRY_LIST } from "@/data/industries";
import { PRODUCT_MENU, FEATURE_MENU, INTEGRATIONS, FREE_TOOLS } from "@/data/catalog";
import DemoFormDialog from "@/components/DemoFormDialog";

const RESOURCES = [
  ...FREE_TOOLS.map((t) => ({ ...t, desc: "Free tool" })),
  { to: "/docs", label: "Docs & API", icon: BookOpen, desc: "Portal guide and API reference" },
  { to: "/#faq", label: "FAQs", icon: HelpCircle, desc: "Answers to common questions" },
  { to: "/contact", label: "Help & Support", icon: LifeBuoy, desc: "Talk to our team" },
  { to: "/contact", label: "Contact Sales", icon: Mail, desc: BRAND.phone },
];

const triggerCls = "flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 outline-none data-[state=open]:text-slate-900";

function MegaItem({ to, icon: Icon, title, desc, testId }) {
  return (
    <DropdownMenuItem asChild>
      <Link to={to} className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer" data-testid={testId}>
        <span className="mt-0.5 w-8 h-8 shrink-0 grid place-items-center rounded-md bg-[#25D366]/10 text-[#25D366]">
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-900">{title}</span>
          {desc && <span className="block text-xs text-slate-500 leading-snug mt-0.5">{desc}</span>}
        </span>
      </Link>
    </DropdownMenuItem>
  );
}

function AllLink({ to, label }) {
  return (
    <>
      <DropdownMenuSeparator className="col-span-full" />
      <DropdownMenuItem asChild className="col-span-full">
        <Link to={to} className="flex items-center justify-between p-2.5 rounded-lg text-sm font-semibold text-[#25D366] cursor-pointer">
          {label} <ArrowRight className="w-4 h-4" />
        </Link>
      </DropdownMenuItem>
    </>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const navLink = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-slate-900" : "text-slate-700 hover:text-slate-900"}`;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo-link">
            <img src={BRAND.logoWebp} alt="GrabNChat – WhatsApp Business API platform" width="176" height="36" className="h-9 w-auto" fetchpriority="high" />
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger className={triggerCls} data-testid="nav-products">Products <ChevronDown className="w-4 h-4" /></DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[640px] p-2 grid grid-cols-2 gap-x-1">
                {PRODUCT_MENU.map((p) => (
                  <MegaItem key={p.slug} to={`/products/${p.slug}`} icon={p.icon} title={p.title} desc={p.short} testId={`nav-product-${p.slug}`} />
                ))}
                <AllLink to="/features" label="See all products & features" />
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={triggerCls} data-testid="nav-features">Features <ChevronDown className="w-4 h-4" /></DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[640px] p-2 grid grid-cols-2 gap-x-1">
                {FEATURE_MENU.map((p) => (
                  <MegaItem key={p.slug} to={`/products/${p.slug}`} icon={p.icon} title={p.title} desc={p.short} testId={`nav-feature-${p.slug}`} />
                ))}
                <MegaItem to="/features" icon={LayoutGrid} title="All features" desc="Everything GrabNChat can do" testId="nav-feature-all" />
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={triggerCls} data-testid="nav-industries">Industries <ChevronDown className="w-4 h-4" /></DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 p-2">
                {INDUSTRY_LIST.map((i) => (
                  <DropdownMenuItem key={i.slug} asChild>
                    <Link to={`/industries/${i.slug}`} className="block p-2 rounded-lg text-sm font-medium text-slate-900" data-testid={`nav-industry-${i.slug}`}>{i.title}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={triggerCls} data-testid="nav-integrations">Integrations <ChevronDown className="w-4 h-4" /></DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[560px] p-2 grid grid-cols-2 gap-x-1">
                {INTEGRATIONS.map((it) => (
                  <MegaItem key={it.slug} to={`/integrations#${it.slug}`} icon={it.icon} title={it.name} desc={it.category} testId={`nav-integration-${it.slug}`} />
                ))}
                <AllLink to="/integrations" label="Explore all integrations" />
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={triggerCls} data-testid="nav-resources">Resources <ChevronDown className="w-4 h-4" /></DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 p-2">
                {RESOURCES.map((r) => (
                  <MegaItem key={r.label} to={r.to} icon={r.icon} title={r.label} desc={r.desc} testId={`nav-resource-${r.label.toLowerCase().replace(/\W+/g, "-")}`} />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink to="/pricing" className={navLink} data-testid="nav-pricing">Pricing</NavLink>
          </nav>

          <div className="hidden xl:flex items-center gap-3 shrink-0">
            <a href={`${BRAND.appUrl}/login`} className="text-sm font-medium text-slate-700 hover:text-slate-900" data-testid="nav-login">Login</a>
            <DemoFormDialog trigger={
              <Button variant="outline" className="rounded-full px-5 h-10 font-semibold border-slate-300" data-testid="nav-book-demo-btn">Book Demo</Button>
            } />
            <a href={`${BRAND.appUrl}/login`} data-testid="nav-start-free-btn">
              <Button className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-5 h-10 font-semibold">Start Free Trial</Button>
            </a>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="xl:hidden p-2 rounded-md border border-slate-200" data-testid="nav-mobile-toggle" aria-label="Open menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:w-96 overflow-y-auto">
              <div className="mt-6 flex flex-col gap-2">
                <MobileGroup title="Products" items={PRODUCT_MENU.map((p) => ({ to: `/products/${p.slug}`, label: p.title }))} onClick={close} />
                <MobileGroup title="Features" items={[...FEATURE_MENU.map((p) => ({ to: `/products/${p.slug}`, label: p.title })), { to: "/features", label: "All features" }]} onClick={close} />
                <MobileGroup title="Industries" items={INDUSTRY_LIST.map((i) => ({ to: `/industries/${i.slug}`, label: i.title }))} onClick={close} />
                <MobileGroup title="Integrations" items={[...INTEGRATIONS.map((i) => ({ to: `/integrations#${i.slug}`, label: i.name })), { to: "/integrations", label: "All integrations" }]} onClick={close} />
                <MobileGroup title="Resources" items={RESOURCES.map((r) => ({ to: r.to, label: r.label }))} onClick={close} />
                <Link onClick={close} to="/pricing" className="px-3 py-3 rounded-lg text-base font-semibold text-slate-900 hover:bg-slate-50" data-testid="m-nav-pricing">Pricing</Link>
                <a href={`${BRAND.appUrl}/login`} className="px-3 py-3 rounded-lg text-base font-semibold text-slate-900 hover:bg-slate-50" data-testid="m-nav-login">Login</a>
                <a href={`${BRAND.appUrl}/login`} className="mt-4">
                  <Button className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full h-11 font-semibold">Start Free Trial</Button>
                </a>
                <DemoFormDialog trigger={
                  <Button variant="outline" className="rounded-full h-11 font-semibold" data-testid="m-nav-book-demo-btn">Book Free Demo</Button>
                } />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function MobileGroup({ title, items, onClick }) {
  return (
    <details className="rounded-lg border border-slate-200">
      <summary className="cursor-pointer px-3 py-3 text-base font-semibold text-slate-900 flex items-center justify-between">
        {title}<ChevronDown className="w-4 h-4" />
      </summary>
      <div className="pb-2">
        {items.map((it) => (
          <Link key={it.to + it.label} onClick={onClick} to={it.to} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
            {it.label}
          </Link>
        ))}
      </div>
    </details>
  );
}
